// Per-country geometry WITH identity for the Locations globe — a sibling of `globe-borders.ts`
// (which only produces identity-less merged border lines via topojson `mesh`). Here we use
// topojson `feature()` to keep each country as its own set of polygon rings plus its ISO id and
// name, so the Locations globe can fill ONE country and detect WHICH country is under the cursor.
//
// Reuses the exact lon/lat → unit-sphere convention from `globe-borders.ts`, so points project
// with the same camera math as the home globe. Built ONCE at module scope and cached.

import { feature } from "topojson-client";
import worldData from "world-atlas/countries-110m.json";
import { lonLatToUnit, type UPoint } from "@/lib/globe-borders";

const DEG = Math.PI / 180;

// Minimal structural types — avoids a geojson type import, mirrors the casting style in globe-borders.ts.
type LonLat = [number, number];
interface GeoFeature {
  id?: string | number;
  properties?: { name?: string } | null;
  geometry:
    | { type: "Polygon"; coordinates: LonLat[][] }
    | { type: "MultiPolygon"; coordinates: LonLat[][][] }
    | null;
}
interface GeoFeatureCollection {
  features: GeoFeature[];
}

const topo = worldData as unknown as Parameters<typeof feature>[0];

export interface CountryRing {
  /** Ring as unit-sphere points — used for projection and fill (frame-independent). */
  unit: UPoint[];
  /** Ring as [lon,lat] in the TEST frame (unwrapped past the antimeridian when `crosses`). */
  test: LonLat[];
  /** When true the ring straddles ±180°, so a query longitude must be unwrapped (lon<0 → +360). */
  crosses: boolean;
  lonMin: number;
  lonMax: number;
  latMin: number;
  latMax: number;
}

export interface CountryFeature {
  /** ISO 3166-1 numeric code as a string, e.g. "840" for the United States. */
  id: string;
  /** Display name from the atlas, e.g. "United States of America". */
  name: string;
  /** Outer rings only (holes are dropped — negligible at 110m for a marketing globe). */
  rings: CountryRing[];
  /** Spherical centroid of the largest ring, [lon, lat] in degrees. */
  centroidLonLat: LonLat;
}

/** Thin out interior vertices closer than `stepDeg` in both lon and lat — same rule as globe-borders. */
function decimate(ring: LonLat[], stepDeg: number): LonLat[] {
  const out: LonLat[] = [];
  let prevLon = NaN;
  let prevLat = NaN;
  for (let i = 0; i < ring.length; i++) {
    const [lon, lat] = ring[i];
    const isEnd = i === 0 || i === ring.length - 1;
    if (!isEnd && Math.abs(lon - prevLon) < stepDeg && Math.abs(lat - prevLat) < stepDeg) continue;
    prevLon = lon;
    prevLat = lat;
    out.push([lon, lat]);
  }
  return out;
}

function buildRing(ring: LonLat[], stepDeg: number): CountryRing | null {
  // Only thin DENSE rings (long coastlines). Small countries have few vertices and would collapse
  // below a drawable/testable polygon if decimated — keep those intact (e.g. Fiji, island states).
  const pts = ring.length > 80 ? decimate(ring, stepDeg) : ring.slice();
  if (pts.length < 3) return null;

  // Antimeridian detection: any edge jumping more than 180° of longitude wraps the seam.
  let crosses = false;
  for (let i = 1; i < pts.length; i++) {
    if (Math.abs(pts[i][0] - pts[i - 1][0]) > 180) {
      crosses = true;
      break;
    }
  }

  // Test frame: for crossing rings shift negative longitudes by +360 so the ring is contiguous.
  const test: LonLat[] = crosses ? pts.map(([lon, lat]) => [lon < 0 ? lon + 360 : lon, lat]) : pts;

  let lonMin = Infinity;
  let lonMax = -Infinity;
  let latMin = Infinity;
  let latMax = -Infinity;
  for (const [lon, lat] of test) {
    if (lon < lonMin) lonMin = lon;
    if (lon > lonMax) lonMax = lon;
    if (lat < latMin) latMin = lat;
    if (lat > latMax) latMax = lat;
  }

  const unit = pts.map(([lon, lat]) => lonLatToUnit(lon, lat));
  return { unit, test, crosses, lonMin, lonMax, latMin, latMax };
}

/** Spherical centroid (mean of unit vectors) of the ring with the most vertices, back to [lon,lat]°. */
function centroidOf(rings: CountryRing[]): LonLat {
  let largest = rings[0];
  for (const r of rings) if (r.unit.length > largest.unit.length) largest = r;
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of largest.unit) {
    x += p.ux;
    y += p.uy;
    z += p.uz;
  }
  const len = Math.hypot(x, y, z) || 1;
  x /= len;
  y /= len;
  z /= len;
  const lat = Math.asin(Math.max(-1, Math.min(1, y))) / DEG;
  const lon = Math.atan2(z, x) / DEG;
  return [lon, lat];
}

function outerRings(geometry: GeoFeature["geometry"]): LonLat[][] {
  if (!geometry) return [];
  if (geometry.type === "Polygon")
    return geometry.coordinates.length ? [geometry.coordinates[0]] : [];
  // MultiPolygon: take the outer ring (index 0) of every polygon.
  return geometry.coordinates.map((poly) => poly[0]).filter(Boolean);
}

export function buildCountryFeatures(stepDeg = 1.2): CountryFeature[] {
  const fc = feature(
    topo,
    topo.objects.countries as unknown as Parameters<typeof feature>[1]
  ) as unknown as GeoFeatureCollection;

  const result: CountryFeature[] = [];
  for (const f of fc.features) {
    const rings: CountryRing[] = [];
    for (const ring of outerRings(f.geometry)) {
      const built = buildRing(ring, stepDeg);
      if (built) rings.push(built);
    }
    if (rings.length === 0) continue;
    result.push({
      id: String(f.id ?? ""),
      name: f.properties?.name ?? "Unknown",
      rings,
      centroidLonLat: centroidOf(rings),
    });
  }
  return result;
}

export const COUNTRY_FEATURES = buildCountryFeatures();
export const COUNTRY_BY_ID = new Map(COUNTRY_FEATURES.map((f) => [f.id, f]));
